// components/ExcelReuploadModal.jsx
// Styles: import "excel-modal.css" once in your index.js

import React, { useEffect, useRef, useState, useCallback } from "react";
import { Modal, ModalBody } from "reactstrap";
import * as XLSX from "xlsx";
import { Label } from "reactstrap";;
import api from "../api";
import {
  FileSpreadsheet,
  UploadCloud,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import { showSuccess, showError } from "./AppToast";

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const MANDATORY_SERVER_FIELDS = new Set([
  "email", "e_mail", "mail",
  "phone", "mobile", "cell", "contact", "tel", "ph_no", "phno",
  "amount", "fee", "fees",
]);

// ─────────────────────────────────────────────────────────────────────────────
// normalizeHeader — handles camelCase + spaces + special chars
// e.g. "admissionNumber" → "admission_number", "Student Name" → "student_name"
// ─────────────────────────────────────────────────────────────────────────────
function normalizeHeader(str) {
  if (!str) return "";
  return String(str)
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[''`]/g, "")
    .replace(/[^a-z0-9\s_]/g, " ")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

// Returns true if the normalized header is a mandatory server field
function isMandatoryField(normalizedHeader) {
  if (MANDATORY_SERVER_FIELDS.has(normalizedHeader)) return true;
  for (const m of MANDATORY_SERVER_FIELDS) {
    if (normalizedHeader.startsWith(m)) return true;
  }
  return false;
}

// ─────────────────────────────────────────────────────────────────────────────
// Local helpers
// ─────────────────────────────────────────────────────────────────────────────

const MAX_CELL_LENGTH = 250;

function validateLengths(rawRows) {
  const headers = rawRows[0] || [];
  for (const h of headers) {
    const len = h != null ? String(h).trim().length : 0;
    if (len > MAX_CELL_LENGTH) {
      throw new Error("Column headers cannot exceed 250 characters.");
    }
  }
  for (let r = 1; r < rawRows.length; r++) {
    const row = rawRows[r];
    for (let c = 0; c < row.length; c++) {
      const val = row[c];
      const len = val != null ? String(val).trim().length : 0;

      if (len > MAX_CELL_LENGTH) {
        throw new Error(
          `Row ${r + 1}, Column ${c + 1} has a value longer than 250 characters.`,
        );
      }
    }
  }
}

function parseExcelRows(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: false });

        if (!rows.length) return reject(new Error("File is empty"));

        // Guard check
        try {
          validateLengths(rows);
        } catch (err) {
          return reject(err);
        }

        // Return full rows as objects
        resolve(XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false }));
      } catch (err) { reject(err); }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });
}

function parseExcelHeaders(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (!rows.length) return reject(new Error("File is empty"));

        // Guard check
        try {
          validateLengths(rows);
        } catch (err) {
          return reject(err);
        }

        const headers = (rows[0] || [])
          .map((h) => normalizeHeader(h))
          .filter(Boolean);

        resolve(headers);
      } catch (err) { reject(err); }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });
}


function fmtSize(bytes) {
  return bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(1)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

// Normalize all keys in every row (this is the key fix)
function normalizeExcelData(rows) {
  return rows.map(row => {
    const normalizedRow = {};
    Object.keys(row).forEach(originalKey => {
      const normalizedKey = normalizeHeader(originalKey);
      normalizedRow[normalizedKey] = row[originalKey];
    });
    return normalizedRow;
  });
}

function pollJobStatus({ formId, onProgress, signal }) {
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = async () => {
      if (signal?.aborted) return reject(new Error("cancelled"));
      if (Date.now() - started > POLL_TIMEOUT_MS)
        return reject(new Error("Job timed out after 5 minutes."));

      try {
        const token = localStorage.getItem("jwt");

        const { data } = await api.post(
          `/bulk/job-status/${formId}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const status = String(data?.status ?? "").toUpperCase();
        onProgress({
          processed: data?.processed ?? 0,
          success: data?.success ?? 0,
          failed: data?.failed ?? 0,
          percentage: data?.percentage ?? 0,
          status,
        });

        if (status === "COMPLETED") return resolve(data);
        if (status === "FAILED")
          return reject(new Error(
            data?.reason && data.reason !== "NA" ? data.reason : "Processing failed on the server."
          ));

        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (err) {
        if (signal?.aborted) return reject(new Error("cancelled"));
        reject(err);
      }
    };
    setTimeout(tick, 1500);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Upload phases
// ─────────────────────────────────────────────────────────────────────────────

const PHASE = {
  IDLE: "idle",
  VALIDATING: "validating",
  UPLOADING: "uploading",
  PROCESSING: "processing",
  DONE: "done",
  ERROR: "error",
};

// ─────────────────────────────────────────────────────────────────────────────
// ExcelReuploadModal
// ─────────────────────────────────────────────────────────────────────────────

export default function ExcelReuploadModal({
  isOpen,
  toggle,
  formId,
  slug,
  onSuccess,
}) {
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [fileHeaders, setFileHeaders] = useState(null);
  const [serverHeaders, setServerHeaders] = useState([]);
  const [headerStatus, setHeaderStatus] = useState(null);
  const [mismatchInfo, setMismatchInfo] = useState(null);
  const [phase, setPhase] = useState(PHASE.IDLE);
  const [phaseMsg, setPhaseMsg] = useState("");
  const [jobStats, setJobStats] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [rowsLength, setRowsLength] = useState("");

  const fileInputRef = useRef(null);
  const abortRef = useRef(null);

  const isBusy = [PHASE.VALIDATING, PHASE.UPLOADING, PHASE.PROCESSING].includes(phase);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setFileHeaders(null);
      setServerHeaders([]);
      setHeaderStatus(null);
      setMismatchInfo(null);
      setPhase(PHASE.IDLE);
      setPhaseMsg("");
      setJobStats(null);
      setUploadProgress(0);
      setErrorMsg("");
    } else {
      abortRef.current?.abort();
    }
  }, [isOpen]);

  // File pick + header validation
  const handleFile = useCallback(async (f) => {
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      showError("Please upload a valid .xlsx, .xls, or .csv file.");
      return;
    }

    setFile(f);
    setHeaderStatus(null);
    setMismatchInfo(null);
    setFileHeaders(null);
    setErrorMsg("");
    setPhase(PHASE.VALIDATING);
    setPhaseMsg("Fetching required columns from server…");

    try {
      const localRawHeaders = await parseExcelHeaders(f);
      setFileHeaders(localRawHeaders);

      if (localRawHeaders.length === 0) {
        showError("The uploaded file has no headers. Please upload a valid Excel/CSV file with headers.");
        setFile(null);
        return;
      }

      const token = localStorage.getItem("jwt");

      const res = await api.post(
        `/bulk/rowHeaders?slug=${slug}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = res.data;
      const srvRawHeaders = Array.isArray(data) ? data : data?.headers ?? [];
      setServerHeaders(srvRawHeaders);

      // Normalize and compare (excluding mandatory fields)
      const normLocal = localRawHeaders.map(normalizeHeader);
      const normSrv = srvRawHeaders.map(normalizeHeader);

      const filteredNormSrv = normSrv.filter((h) => !isMandatoryField(h));
      const filteredSrvRaw = srvRawHeaders.filter((_, i) => !isMandatoryField(normSrv[i]));

      const filteredNormLocal = normLocal.filter((h) => !isMandatoryField(h));
      const filteredLocalRaw = localRawHeaders.filter((_, i) => !isMandatoryField(normLocal[i]));

      const missing = filteredSrvRaw.filter((_, i) => !filteredNormLocal.includes(filteredNormSrv[i]));
      const extra = filteredLocalRaw.filter((_, i) => !filteredNormSrv.includes(filteredNormLocal[i]));

      if (!missing.length && !extra.length) {
        setHeaderStatus("ok");
      } else {
        setHeaderStatus("mismatch");
        setMismatchInfo({ missing, extra });
      }
    } catch (err) {
      showError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Could not validate headers. Please try again."
      );
      setFile(null);
    } finally {
      setPhase(PHASE.IDLE);
      setPhaseMsg("");
    }
  }, [slug]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  // Upload with normalized column names
  const handleUpload = async () => {
    if (!file || headerStatus !== "ok") return;

    const ac = new AbortController();
    abortRef.current = ac;

    setErrorMsg("");
    setJobStats(null);
    setUploadProgress(0);

    try {
      let rows = await parseExcelRows(file);

      if (rows.length === 0) {
        showError("The uploaded Excel/CSV file is empty or contains only headers. Please add at least one data row.");
        return;
      }

      if (rows.length > 2000) {
        showError(`Maximum 2000 rows allowed. Your file has ${rows.length} rows.`);
        return;
      }

      setRowsLength(rows.length);

      // === KEY FIX: Normalize all column names in the data ===
      rows = normalizeExcelData(rows);

      setPhase(PHASE.UPLOADING);
      setPhaseMsg("Uploading master data…");
      setUploadProgress(15);
      // const uploadStartTime = new Date();

      // const searchKey = localStorage.getItem("excel_search_key") || "";
      const token = localStorage.getItem("jwt");

      const res = await api.post(
        "/bulk/mastersUpload",
        {
          excelValues: JSON.stringify(rows),   // Now sending normalized keys
          formId,
          // searchKey,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = res.data;

      // if(data ?.error){
      //   throw new Error(data.error);
      // }
      if (ac.signal.aborted) return;

      setUploadProgress(40);
      setPhase(PHASE.PROCESSING);
      setPhaseMsg("Processing records…");

      await pollJobStatus({
        formId,
        signal: ac.signal,
        // uploadStartTime,
        onProgress: (stats) => {
          setJobStats(stats);
          setUploadProgress(40 + Math.floor(stats.percentage * 0.6));
          setPhaseMsg(`Processing… (${stats.processed} records) (${stats.percentage}%)`);
        },
      });

      if (ac.signal.aborted) return;

      setUploadProgress(100);
      setPhase(PHASE.DONE);
      setJobStats((prev) => prev ? { ...prev, percentage: 100 } : null);

      await new Promise((r) => setTimeout(r, 500));

      showSuccess("Data uploaded and processed successfully!");
      onSuccess(rows);
    } catch (err) {
      if (ac.signal.aborted) return;
      console.error("Upload failed:", err);

      const errorMsg = err.response?.data?.error || "Upload failed. Please try again.";
      setErrorMsg(errorMsg);
      setPhase(PHASE.ERROR);

      showError(errorMsg); // optional toast
    }

    // } catch (err) {
    //   if (ac.signal.aborted) return;
    //   console.error("Upload failed:", err);
    //   showError("Upload failed. Please try again.");
    // }
  };

  // ... (rest of the component is unchanged)

  const isOk = headerStatus === "ok";
  const isMismatch = headerStatus === "mismatch";
  const isValidating = phase === PHASE.VALIDATING;
  const isProcessing = phase === PHASE.PROCESSING;
  const isError = phase === PHASE.ERROR;

  const dropZoneClass = [
    "em-dropzone",
    dragOver ? "em-dropzone-drag" : "",
    phase === PHASE.DONE ? "em-dropzone-ok" : "",
    isOk ? "em-dropzone-ok" : "",
    isMismatch || isError ? "em-dropzone-err" : "",
  ].filter(Boolean).join(" ");

  // const fileIconTileClass = [
  //   "em-file-icon-tile",
  //   isOk ? "em-file-icon-tile-ok"
  //     : (isMismatch || isError) ? "em-file-icon-tile-mismatch"
  //       : "em-file-icon-tile-idle",
  // ].join(" ");
  const fileIconTileClass = [
  "em-file-icon-tile",
  isError ? "em-file-icon-tile-error"
    : isOk && file ? "em-file-icon-tile-ok"
    : "em-file-icon-tile-idle",
].join(" ");


  // const fileIconColor = isOk ? "#16a34a" : (isMismatch || isError) ? "#dc2626" : "#6b7280";
const fileIconColor =
  isError ? "#dc2626" :          // red when error
  isOk && file ? "#16a34a" :     // green only when ok AND a file is present
  "#6366f1";                     // blue default (initial state / reset)

  return (
    <Modal isOpen={isOpen} toggle={!isBusy ? toggle : undefined} centered size="lg">
      {/* Header */}
      <div className="em-header">
        <div className="em-header-left">
          <div className="em-header-icon-tile">
            <FileSpreadsheet size={22} color="#4ade80" />
          </div>
          <div>
            <div className="em-header-title">Re-upload Master Data</div>
          </div>
        </div>
        {!isBusy && (
          <button className="em-header-close-btn" onClick={toggle}>×</button>
        )}
      </div>

      {/* Body - unchanged except for the upload logic above */}
      <ModalBody className="em-body">

        {serverHeaders.length > 0 && (
          <div className="em-headers-panel">
            <div className="em-headers-panel-label">
              <div className="em-headers-panel-info-dot">i</div>
              <span className="em-headers-panel-text">
                Required column headers for this form:
              </span>
            </div>
            <div className="em-headers-panel-tags">
              {serverHeaders
                .filter((h) => !isMandatoryField(normalizeHeader(h)))
                .map((h) => (
                  <span key={h} className="em-tag em-tag-blue">{h}</span>
                ))}
            </div>
          </div>
        )}

        <div className="em-dropzone-wrap">
          <Label style={{ fontSize: "0.85rem" }}>
            In the Excel file you upload, ensure that every value defined under the
            <strong> Search Key</strong> column does not exceed <strong>100 characters</strong></Label>
          {/* Drop zone UI - unchanged */}
          <div
            className={dropZoneClass}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => !isBusy && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="em-file-input"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />

            {(isValidating || isBusy) && (
              <div className="em-dz-state">
                <div className="em-dz-scan-ring">
                  <div className="em-dz-scan-bar" />
                </div>
                <span className="em-dz-state-title">
                  {isValidating ? "Validating columns…" : isProcessing ? "Processing records…" : "Uploading…"}
                </span>
                <span className="em-dz-state-sub">{phaseMsg}</span>
              </div>
            )}

            {!isValidating && !isBusy && file && (
              <div className="em-file-content">
                <div className="em-file-row">
                  <div className={fileIconTileClass}>
                    <FileSpreadsheet size={26} color={fileIconColor} />
                  </div>
                  <div className="em-file-info">
                    <div className="em-file-name">{file.name}</div>
                    <div className="em-file-meta">
                      {fmtSize(file.size)}
                      {fileHeaders && (
                        <span>
                          {" · "}
                          <strong className="em-file-meta-highlight">{fileHeaders.length}</strong>
                          {" columns"}
                        </span>
                      )}
                      <span>
                        {" · "}
                        <strong className="em-file-meta-highlight">{rowsLength}</strong>
                        {" rows"}
                      </span>
                    </div>
                  </div>
                  {isOk && !isError &&(
                    <div className="em-file-status-pill em-file-status-pill-ok">
                      <CheckCircle2 size={14} color="#16a34a" />
                      <span className="em-file-status-label-ok">Verified</span>
                    </div>
                  )}
                  {isMismatch && (
                    <div className="em-file-status-pill em-file-status-pill-mismatch">
                      <AlertTriangle size={14} color="#d97706" />
                      <span className="em-file-status-label-mismatch">Mismatch</span>
                    </div>
                  )}
                </div>
                <div className="em-file-change-hint">Click anywhere to choose a different file</div>
              </div>
            )}

            {!isValidating && !isBusy && !file && (
              <div className="em-dz-state">
                <div className="em-dz-upload-icon">
                  <UploadCloud size={36} color="#6366f1" />
                </div>
                <span className="em-dz-state-title">
                  {dragOver ? "Release to upload" : "Drag & drop here"}
                </span>
                <span className="em-dz-state-sub">
                  or <span className="em-dz-browse">browse file</span>
                </span>
                <div className="em-dz-formats">
                  <span className="em-fmt-pill">XLSX</span>
                  <span className="em-fmt-pill">XLS</span>
                  <span className="em-fmt-pill">CSV</span>
                </div>
              </div>
            )}
          </div>

          {isMismatch && mismatchInfo && (
            <div className="em-mismatch-panel">
              <div className="em-mismatch-panel-header">
                <AlertTriangle size={14} color="#b45309" />
                <span>Column mismatch — fix your file and re-upload</span>
              </div>
              <div className="em-mismatch-panel-body">
                {mismatchInfo.missing.length > 0 && (
                  <div className="em-mismatch-group">
                    <span className="em-mismatch-group-label em-mismatch-group-label-red">Missing</span>
                    <div className="em-mismatch-chips">
                      {mismatchInfo.missing.map((h) => (
                        <span key={h} className="em-chip-red">
                          <XCircle size={10} /> {h}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {mismatchInfo.extra.length > 0 && (
                  <div className="em-mismatch-group">
                    <span className="em-mismatch-group-label em-mismatch-group-label-amber">Extra</span>
                    <div className="em-mismatch-chips">
                      {mismatchInfo.extra.map((h) => (
                        <span key={h} className="em-chip-amber">{h}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {jobStats && jobStats.success >= 50 && (
            <div className="em-job-stats">
              <div className="em-job-stat">
                <span className="em-job-stat-num em-job-stat-num-blue">{jobStats.processed}</span>
                <span className="em-job-stat-label">Processed</span>
              </div>
              <div className="em-job-stat-divider" />
              <div className="em-job-stat">
                <span className="em-job-stat-num em-job-stat-num-green">{jobStats.success}</span>
                <span className="em-job-stat-label">Success</span>
              </div>
              <div className="em-job-stat-divider" />
              <div className="em-job-stat">
                <span className={`em-job-stat-num ${jobStats.failed > 0 ? "em-job-stat-num-red" : "em-job-stat-num-grey"}`}>
                  {jobStats.failed}
                </span>
                <span className="em-job-stat-label">Failed</span>
              </div>
            </div>
          )}

          {isBusy && (
            <div className="em-progress-wrap">
              <div className="em-progress-track">
                <div className="em-progress-fill" style={{ width: `${uploadProgress}%` }} />
              </div>
              <span className="em-progress-label">{uploadProgress}%</span>
            </div>
          )}

          {isOk && file && !isBusy && !isError && (
            <div className="em-success-bar">
              <CheckCircle2 size={15} color="#16a34a" />
              <span>All columns verified — ready to upload</span>
            </div>
          )}
        </div>
        {phase === PHASE.ERROR && (
          <div className="repload-fs-excel-fail-card">
            <div className="repload-fs-excel-fail-icon-wrap">
              <AlertTriangle size={18} color="#d97706" />
            </div>
            <div className="repload-fs-excel-fail-content">
              <div className="repload-fs-excel-fail-title">Excel processing failed</div>
              <div className="repload-fs-excel-fail-desc">
                {errorMsg || "Your form was saved. The data processing failed — retry below."}
              </div>
            </div>
            <button
              className="repload-fs-excel-fail-retry-btn"
              onClick={() => {
                // reset everything back to initial state
                setPhase(null);
                setErrorMsg("");
                setFile(null);          // clear the previously selected file
                setRowsLength(0);       // reset row count
                setUploadProgress(0);   // reset progress bar
                  setHeaderStatus(null);
              }}
            >
              <RefreshCw size={13} />
              Re‑upload
            </button>

          </div>
        )}
      </ModalBody>

      {/* Footer */}
      <div className="em-footer">
        <div className="em-footer-hint">
          {isBusy ? (
            <span className="em-footer-hint-uploading">{phaseMsg || "Please wait…"}</span>
          ) : isError ? (
      // when error, show nothing or a short error hint if you want
      null)
          : file && isOk ? (
            <span className="em-footer-hint-ok">
              <CheckCircle2 size={15} color="#16a34a" /> Ready to upload
            </span>
          ) : file && isMismatch ? (
            <span className="em-footer-hint-mismatch">⚠ Fix column mismatches first</span>
          ) : (
            "No file selected • Must contain at least 1 data row"
          )}
        </div>
        <div className="em-footer-actions">
          <button className="em-cancel-btn" onClick={toggle} disabled={isBusy}>
            Cancel
          </button>
          <button
            className="em-upload-btn"
            disabled={!file || !isOk || isBusy}
            onClick={handleUpload}
          >
            {isBusy ? (
              <>
                <div className="em-upload-btn-spinner" />
                {isProcessing ? "Processing…" : "Uploading…"}
              </>
            ) : isError ? (
              <><RefreshCw size={15} /> Retry</>
            ) : (
              <>Upload Data <ArrowRight size={15} /></>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}